import ReactDOM from "react-dom/client";
import "./index.css";
import "./i18n";
import i18n from "./i18n";
import App from "./App";
import { registerPwa } from "./pwa";
import { QueryClient, QueryClientProvider } from "react-query";
import { SnackbarProvider, enqueueSnackbar } from "notistack";
import { ErrorSnackbarIcon, SuccessSnackbarIcon } from "./icons";
import { InfoRounded } from "@mui/icons-material";
import { useEffect } from "react";
import {
  NavigationProvider,
  useNavigation,
} from "./contexts/NavigationContext";
import { ThemeModeProvider } from "./contexts/ThemeModeContext";
import { AxiosError } from "axios";

function isSilentStatus(status?: number) {
  // 401 — авторизация (interceptor сам сделает refresh)
  // 403 — доступ запрещён (ожидаемо для некоторых страниц)
  // 404 — ресурс отсутствует (ожидаемо, когда, например, ещё нет курса пары)
  // 503 — maintenance (уже редирект на /maintenance)
  return status === 401 || status === 403 || status === 404 || status === 503;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      onError(error: unknown) {
        const axiosError = error as AxiosError<{ detail: string; message: string }>;
        const status = axiosError?.response?.status;
        // eslint-disable-next-line no-console
        console.warn("[query error]", axiosError?.config?.url, status, axiosError?.message);
        if (isSilentStatus(status)) return;
        enqueueSnackbar(i18n.t("errors:generic"), {
          variant: "error",
        });
      },
    },
    mutations: {
      onError(error: unknown) {
        const axiosError = error as AxiosError<{ detail?: string; message?: string }>;
        const status = axiosError?.response?.status;
        // eslint-disable-next-line no-console
        console.warn("[mutation error]", axiosError?.config?.url, status, axiosError?.message);
        if (status === 503) return;
        const data = axiosError?.response?.data;
        const detail = data?.detail || data?.message;
        if (status === 422 && detail) {
          enqueueSnackbar(detail, { variant: "error" });
        } else if (status === 400 && detail) {
          enqueueSnackbar(detail, { variant: "error" });
        } else if (status === 401 || status === 403) {
          // Не спамим — просто игнор, refresh/маршрутизация разберётся
          return;
        } else {
          enqueueSnackbar(i18n.t("errors:generic"), {
            variant: "error",
          });
        }
      },
    },
  },
});

const SnackbarWrapper = () => {
  const { isNavigationVisible } = useNavigation();

  // Смещение контейнера snackbar'ов (чтобы не залезал на bottom-nav) держим
  // в CSS-классе на <body>, чтобы marginBottom не размножался на КАЖДЫЙ
  // snackbar-item — иначе они разъезжаются по вертикали.
  useEffect(() => {
    const cls = "zero-has-bottom-nav";
    if (isNavigationVisible) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [isNavigationVisible]);

  return (
    <SnackbarProvider
      anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      autoHideDuration={2500}
      disableWindowBlurListener
      preventDuplicate
      maxSnack={3}
      dense
      // Кастомный class на контейнере — через него в CSS поднимаем snackbar'ы
      // над bottom-nav. Официальный API notistack v3, `!important` в inline-стилях
      // на sam snack'ах его перебить не может.
      classes={{ containerRoot: "zero-snackbar-container" }}
      // Snackbar должен быть выше bottom-nav (99999) и наших drawer'ов (100010).
      style={{
        backgroundColor: "rgba(41, 41, 41, 1)",
        fontFamily: '"Inter",system-ui',
        borderRadius: "10px",
        padding: "6px 12px",
        fontSize: "14px",
        fontWeight: "500",
        zIndex: 100020,
      }}
      SnackbarProps={{
        style: { zIndex: 100020 },
      }}
      iconVariant={{
        success: <SuccessSnackbarIcon />,
        error: <ErrorSnackbarIcon />,
        warning: (
          <InfoRounded sx={{ marginRight: "5px", color: "rgb(50 158 255)" }} />
        ),
      }}
    >
      <App />
    </SnackbarProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <QueryClientProvider client={queryClient}>
    <ThemeModeProvider>
      <NavigationProvider>
        <SnackbarWrapper />
      </NavigationProvider>
    </ThemeModeProvider>
  </QueryClientProvider>
);

// PWA: регистрируем service worker (только в проде, только в браузере).
registerPwa();
